import { assertNever } from "@redotech/util/type";
import {
  BLUE_YONDER_PROVIDER,
  type DropoffProviderType,
  HAPPY_RETURNS_PROVIDER,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
} from "../dropoff-provider";
import {
  type ProductShipmentGroupFields,
  productMatchesShipmentGroup,
} from "../shipping/shipment-group";
import {
  BlueYonderQrCodeMetadataSchema,
  HappyReturnsQrCodeMetadataSchema,
} from "./dropoff-types";

interface DropoffQrCodeEntry {
  provider?: DropoffProviderType | string | null;
  shipmentGroupId?: string | null;
  qrCode?: string | null;
  qrCodeUrl?: string | null;
  trackingNumber?: string | null;
  providerMetadata?: unknown;
  dropoffFlatRateFee?: number | null;
}

interface ReturnWithDropoffs {
  dropoffs?: {
    chosen?: boolean | null;
    qrCodes: DropoffQrCodeEntry[] | null;
  } | null;
}

export function isDropoffChosen(return_: ReturnWithDropoffs): boolean {
  return !!return_.dropoffs?.chosen;
}

export function getPendingDropoffQrCodes<
  T extends { qrCodeUrl?: string | null },
>(return_: {
  dropoffs?: { chosen?: boolean | null; qrCodes: T[] | null } | null;
}): T[] {
  return (return_.dropoffs?.qrCodes ?? []).filter((entry) => !entry.qrCodeUrl);
}

export function hasPendingDropoffQrCodes(return_: ReturnWithDropoffs): boolean {
  return (
    !!return_.dropoffs?.chosen &&
    (return_.dropoffs.qrCodes ?? []).some((entry) => !entry.qrCodeUrl)
  );
}

export function getDropoffQrCodeUris(return_: ReturnWithDropoffs): string[] {
  if (!isDropoffChosen(return_)) {
    return [];
  }
  return (return_.dropoffs?.qrCodes ?? [])
    .map((qr) => qr.qrCodeUrl)
    .filter((url): url is string => !!url);
}

export function hasCompletedDropoffQrCodes(
  return_: ReturnWithDropoffs,
): boolean {
  const qrCodes = return_.dropoffs?.qrCodes ?? [];
  return (
    !!return_.dropoffs?.chosen &&
    qrCodes.length > 0 &&
    qrCodes.every((entry) => !!entry.qrCodeUrl)
  );
}

/**
 * Finds a dropoff QR code entry by provider and optionally shipmentGroupId.
 */
export function getDropoffQrCodeByProvider(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
  shipmentGroupId?: string,
): DropoffQrCodeEntry | undefined {
  return (return_.dropoffs?.qrCodes ?? []).find(
    (qr) =>
      qr.provider === provider &&
      (!shipmentGroupId || qr.shipmentGroupId === shipmentGroupId),
  );
}

/**
 * Checks if a dropoff QR code exists for the given provider (completed with a URL).
 */
export function hasDropoffQrCodeUrlForProvider(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
): boolean {
  return !!getDropoffQrCodeByProvider(return_, provider)?.qrCodeUrl;
}

/**
 * Gets the QR code URL for a specific provider, optionally filtered by shipmentGroupId.
 */
export function getDropoffQrCodeUrlForProvider(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
  shipmentGroupId?: string,
): string | undefined {
  return (
    getDropoffQrCodeByProvider(return_, provider, shipmentGroupId)?.qrCodeUrl ??
    undefined
  );
}

/**
 * Gets the provider-specific identifier for a dropoff entry.
 * - VIRTUAL: trackingNumber from the qrCode entry
 * - BLUE_YONDER: returnAuthorizationNumber from providerMetadata
 * - RETURN_BEAR: undefined
 */
export function getDropoffIdentifierForProvider(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
  shipmentGroupId?: string,
): string | undefined {
  const entry = getDropoffQrCodeByProvider(return_, provider, shipmentGroupId);
  return entry && dropoffIdentifierForEntry(entry, provider);
}

function dropoffIdentifierForEntry(
  entry: DropoffQrCodeEntry,
  provider: DropoffProviderType,
): string | undefined {
  switch (provider) {
    case VIRTUAL_PROVIDER:
      return entry.trackingNumber ?? undefined;
    case BLUE_YONDER_PROVIDER: {
      const parsed = BlueYonderQrCodeMetadataSchema.safeParse(
        entry.providerMetadata,
      );
      return parsed.data?.returnAuthorizationNumber || undefined;
    }
    case RETURN_BEAR_PROVIDER:
      return undefined;
    case HAPPY_RETURNS_PROVIDER: {
      const parsed = HappyReturnsQrCodeMetadataSchema.safeParse(
        entry.providerMetadata,
      );
      return parsed.data?.rmaId;
    }
    default:
      assertNever(provider);
  }
}

/**
 * Deliberately not {@link dropoffIdentifierForEntry}: BlueYonder registers the
 * box with Inmar under the RA number, but merchants search Inmar by the scanned
 * `labelValue`.
 */
const dropoffDisplayIdentifiers: Record<
  DropoffProviderType,
  (entry: DropoffQrCodeEntry) => string | undefined
> = {
  VIRTUAL: (entry) => entry.trackingNumber ?? undefined,
  BLUE_YONDER: (entry) =>
    BlueYonderQrCodeMetadataSchema.safeParse(entry.providerMetadata).data
      ?.labelValue || undefined,
  RETURN_BEAR: () => undefined,
  HAPPY_RETURNS: (entry) =>
    HappyReturnsQrCodeMetadataSchema.safeParse(entry.providerMetadata).data
      ?.rmaId || undefined,
};

export function getDropoffDisplayIdentifier(
  entry: DropoffQrCodeEntry,
  provider: DropoffProviderType,
): string | undefined {
  return dropoffDisplayIdentifiers[provider](entry);
}

export function hasDropoffIdentifierForProvider(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
): boolean {
  return !!getDropoffIdentifierForProvider(return_, provider);
}

/** Scoped to the product's group so a split return reports each line's own dropoff. */
export function getDropoffIdentifierForProduct(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
  product: ProductShipmentGroupFields,
): string | undefined {
  const entry = (return_.dropoffs?.qrCodes ?? []).find(
    (qr) =>
      qr.provider === provider &&
      productMatchesShipmentGroup(product, qr.shipmentGroupId),
  );
  return entry && dropoffIdentifierForEntry(entry, provider);
}

export function hasDropoffIdentifierForProduct(
  return_: ReturnWithDropoffs,
  provider: DropoffProviderType,
  product: ProductShipmentGroupFields,
): boolean {
  return !!getDropoffIdentifierForProduct(return_, provider, product);
}
